import React from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
  SubTitle
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
  SubTitle
)

export default function BarChart({ 
  data, 
  title, 
  height = 300,
  colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'],
  horizontal = false,
  loading = false,
  error = null,
  onBarClick,
  showValueLabels = false,
  valueFormatter,
  barGap = 0.25, // 0..1 (lower = more gap inside each category)
  categoryGap = 0.35, // 0..1 (lower = more vertical gap between categories in horizontal mode)
  minCategoryHeight = 56 // px per category when horizontal
}) {
  if (loading) {
    return (
      <div className="chart-container">
        <div className="chart-loading">Loading chart...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="chart-container">
        <div className="chart-error">Failed to load chart data</div>
      </div>
    )
  }

  if (!data || !data.labels || data.labels.length === 0) {
    return (
      <div className="chart-container">
        <div className="chart-loading">No data available</div>
      </div>
    )
  }
  const chartData = {
    labels: data.labels,
    datasets: data.datasets.map((dataset, index) => {
      // Sanitize data to ensure numbers (avoid NaN/undefined rendering issues)
      const safeData = Array.isArray(dataset.data)
        ? dataset.data.map(v => {
            const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v
            return Number.isFinite(n) ? n : 0
          })
        : []
      const baseColor = colors[index % colors.length]
      const perBarColors = Array.isArray(dataset.barColors) ? dataset.barColors : null
      const perBarBorders = perBarColors ? perBarColors : null
      const multipleDatasets = Array.isArray(data?.datasets) && data.datasets.length > 1
      const autoBarColors = !perBarColors && !multipleDatasets && Array.isArray(dataset.data) && dataset.data.length > 1 
        ? colors.slice(0, dataset.data.length) 
        : baseColor
      return ({
        ...dataset,
        data: safeData,
        backgroundColor: perBarColors || autoBarColors,
        borderColor: perBarBorders || baseColor,
        borderWidth: 0,
        // For horizontal bars: round only the right side corners; else use uniform radius
        borderRadius: horizontal 
          ? { topLeft: 0, bottomLeft: 0, topRight: 8, bottomRight: 8 }
          : 0,
        borderSkipped: false,
        // When multiple datasets are rendered side-by-side, avoid forcing a fixed
        // barThickness so Chart.js can apply barPercentage and create a visible gap
        // between bars. Keep a fixed thickness only for single-dataset charts.
        barThickness: multipleDatasets ? undefined : 16,
        maxBarThickness: multipleDatasets ? 32 : 40
      })
    })
  }

  // Calculate overall max for numeric axis (used to provide a bit of headroom)
  const overallMax = Array.isArray(chartData?.datasets)
    ? Math.max(0, ...chartData.datasets.flatMap(ds => Array.isArray(ds.data) ? ds.data : [0]))
    : 0

  const titleLines = Array.isArray(title) ? title : [title]
  const mainTitle = titleLines?.[0] || ''
  const subTitle = titleLines?.[1] || null

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? 'y' : 'x',
    animation: {
      duration: 1200,
      easing: 'easeOutQuart',
      delay: (context) => context.dataIndex * 80
    },
    datasets: {
      bar: {
        // Horizontal uses provided gaps; vertical groups have bars touching each other
        categoryPercentage: horizontal ? categoryGap : 0.7,
        barPercentage: horizontal ? barGap : 0.85
      }
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          padding: 24,
          usePointStyle: true,
          pointStyle: 'rect',
          font: {
            size: 13,
            weight: '500',
            family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
          },
          color: '#374151',
          generateLabels: function(chart) {
            // If single dataset with per-bar colors, show legend items for each bar
            const d = chart.data
            const ds = chart.data.datasets?.[0]
            const barColors = Array.isArray(ds?.backgroundColor) ? ds.backgroundColor : null
            if (d?.labels && barColors && barColors.length === d.labels.length && chart.data.datasets.length === 1) {
              return d.labels.map((text, i) => ({
                text,
                fillStyle: barColors[i],
                strokeStyle: barColors[i],
                lineWidth: 2,
                hidden: false,
                index: i
              }))
            }
            // Fallback to default legend behavior
            return ChartJS.defaults.plugins.legend.labels.generateLabels(chart)
          }
        },
        // Make legend behave like plain labels (non-clickable, no pointer cursor)
        onClick: () => {},
        onHover: (event) => {
          if (event?.native?.target?.style) {
            event.native.target.style.cursor = 'default'
          }
        }
      },
      title: {
        display: !!mainTitle,
        text: mainTitle,
        align: 'center',
        font: {
          size: 18,
          weight: '600',
          family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        color: '#111827',
        padding: {
          top: 12,
          bottom: subTitle ? 8 : 18
        }
      },
      subtitle: {
        display: !!subTitle,
        text: subTitle || '',
        align: 'center',
        font: {
          size: 13,
          weight: '500',
          family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        color: '#6b7280',
        padding: {
          bottom: 22
        }
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 12,
        displayColors: true,
        titleFont: {
          size: 13,
          weight: '600',
          family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        bodyFont: {
          size: 12,
          weight: '400',
          family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        padding: 12,
        callbacks: {
          title: function(context) {
            return context[0].label
          },
          label: function(context) {
            const label = context.dataset.label || ''
            const rawVal = horizontal ? context.parsed.x : (context.parsed.y ?? context.parsed.x)
            const n = Number(rawVal)
            const safe = Number.isFinite(n) ? n : 0
            return `${label}: ${safe.toLocaleString()}`
          }
        }
      },
      // custom value labels plugin (lightweight alternative to chartjs-plugin-datalabels)
      valueLabels: {}
    },
    scales: {
      x: {
        grid: {
          display: horizontal ? true : false,
          color: horizontal ? 'rgba(229, 231, 235, 0.3)' : undefined,
          drawBorder: false
        },
        ticks: {
          font: {
            size: 12,
            weight: '500',
            family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
          },
          color: '#6b7280',
          padding: 12,
          autoSkip: horizontal ? true : false,
          maxRotation: 0,
          minRotation: 0,
          stepSize: horizontal ? function(context) {
            const maxValue = Math.max(...context.chart.data.datasets.flatMap(ds => ds.data || []), 0)
            // Dynamic step size based on max value
            if (maxValue <= 10) return 1
            if (maxValue <= 50) return 5
            if (maxValue <= 100) return 10
            return Math.ceil(maxValue / 10)
          } : undefined,
          callback: function(value) {
            if (horizontal) {
              const v = this.getLabelForValue ? Number(this.getLabelForValue(value)) : value
              // For horizontal charts (Performance), ensure clicks show as whole numbers
              if (typeof v === 'number' && !isNaN(v)) {
                return Number.isInteger(v) ? v.toLocaleString() : Math.round(v).toLocaleString()
              }
              return v
            }
            // Vertical mode: format label, stripping common prefixes like "LOT - "
            const raw = this.getLabelForValue ? this.getLabelForValue(value) : value
            return typeof raw === 'string' ? raw.replace(/^\s*LOT\s*-\s*/i, '') : raw
          }
        },
        // Set dynamic max value for horizontal charts (Performance)
        suggestedMax: horizontal ? function(context) {
          const data = context.chart.data.datasets.flatMap(ds => ds.data || [])
          const maxValue = Math.max(...data, 0)
          // If no data or max value is 0, set default scale 0-5
          // Otherwise, add some padding above the max value
          return maxValue === 0 ? 5 : Math.ceil(maxValue * 1.1)
        } : undefined,
        border: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        suggestedMax: horizontal ? undefined : (overallMax + 3),
        grid: {
          color: horizontal ? 'transparent' : 'rgba(229, 231, 235, 0.3)',
          drawBorder: false,
          lineWidth: 1,
          drawTicks: false
        },
        // add extra space before first and after last category
        offset: horizontal ? true : false,
        ticks: {
          font: {
            size: 12,
            weight: '500',
            family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
          },
          color: '#6b7280',
          padding: 14,
          callback: function(value) {
            if (horizontal) {
              // Horizontal mode: categorical labels on y-axis
              return data && data.labels ? data.labels[value] ?? value : value
            }
            // Vertical mode: numeric y values
            const n = Number(value)
            return Number.isFinite(n) ? n.toLocaleString() : value
          }
        },
        border: {
          display: false
        }
      }
    },
    layout: {
      padding: {
        top: 20,
        bottom: 20,
        left: 20,
        right: 20
      }
    },
    elements: {
      bar: {
        hoverBackgroundColor: function(context) {
          const color = context.dataset.backgroundColor
          return color + 'CC'
        },
        hoverBorderColor: function(context) {
          return context.dataset.borderColor
        },
        hoverBorderWidth: 2,
        hoverBorderRadius: horizontal 
          ? { topLeft: 0, bottomLeft: 0, topRight: 8, bottomRight: 8 }
          : 0
      }
    },
    onClick: (evt, elements, chart) => {
      if (!onBarClick) return
      if (elements && elements.length > 0) {
        const el = elements[0]
        const label = chart?.data?.labels?.[el.index]
        onBarClick({ index: el.index, datasetIndex: el.datasetIndex, label })
      }
    }
  }

  // runtime plugin for value labels
  const plugins = []
  if (showValueLabels) {
    plugins.push({
      id: 'valueLabels',
      afterDatasetsDraw(chart) {
        const { ctx } = chart
        ctx.save()
        chart.data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex)
          meta.data.forEach((elem, idx) => {
            const raw = horizontal ? (elem?.$context?.parsed?.x ?? dataset.data[idx]) : dataset.data[idx]
            const n = Number(raw)
            const safeNumber = Number.isFinite(n) ? n : 0
            const text = valueFormatter 
              ? valueFormatter(safeNumber)
              : safeNumber.toLocaleString()
            const { x, y, base } = elem.tooltipPosition()
            ctx.fillStyle = '#1f2937'
            ctx.font = '500 12px Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
            ctx.textAlign = horizontal ? 'left' : 'center'
            ctx.textBaseline = 'middle'
            if (horizontal) {
              const barRight = elem.x
              const padding = 6
              ctx.fillText(text, barRight + padding, y)
            } else {
              ctx.fillText(text, x, y - 10)
            }
          })
        })
        ctx.restore()
      }
    })
  }

  // Auto-height for horizontal charts to ensure visible gaps between bars
  const computedHeight = horizontal && Array.isArray(data?.labels)
    ? Math.max(height, (data.labels.length * minCategoryHeight) + 48) // +padding for axes/title
    : height

  return (
    <div style={{ height: computedHeight, width: '100%' }}>
      <Bar data={chartData} options={options} plugins={plugins} />
    </div>
  )
}
